import moment from 'moment';
import { i18n } from '../../localization/i18n';
import { getFormatPhoneNumber } from '../Utility';
import { isGroupChat } from './ChatHelper';
import {
    MSG_PROCESSING_STATUS_ID,
    MSG_SENT_ACKNOWLEDGE_STATUS_ID,
    MSG_DELIVERED_STATUS_ID,
    MSG_SEEN_STATUS_ID,
    CHAT_TYPE_GROUP,
    GROUP_CREATED,
    GROUP_USER_ADDED,
    GROUP_USER_REMOVED,
    GROUP_USER_LEFT,
    GROUP_PROFILE_INFO_UPDATED,
    GROUP_USER_MADE_ADMIN,
    GROUP_TITLE_UPDATED,
    GROUP_IMAGE_ADDED,
    GROUP_IMAGE_UPDATED,
    GROUP_DESCRIPTION_UPDATED,
    GROUP_DISAPPEAR_UPDATED,
    GROUP_DESCRIPTION_ADDED,
} from './Constant';
import { isLocalUser, getContactNameFromRoster, getDataFromRoster } from './User';
import { getDisappearOptionLabelKey, msgDisappearToOption } from './disappearOptions';

// The shared option label, lower-cased to sit inside the sentence.
const getDisappearLabel = value => i18n.t(getDisappearOptionLabelKey(msgDisappearToOption(value))).toLowerCase();

export const messageNotification = ['sentMessage', 'carbonSentMessage', 'carbonReceiveMessage', 'receiveMessage'];

export const receiveNotification = ['delivered', 'receive', 'carbonDelivered'];

export const messageDeliveryStatus = {
    [MSG_SENT_ACKNOWLEDGE_STATUS_ID]: 'user-status-sent',
    [MSG_DELIVERED_STATUS_ID]: 'user-status-receive',
    [MSG_SEEN_STATUS_ID]: 'user-status',
    [MSG_PROCESSING_STATUS_ID]: 'msg-processing',
};

/**
 * keepin the order of the message delivery status
 * 3 - processing, 0 - sent, 1 - delivered to remote user, 2 - seen by remote user
 */
export const msgStatusOrder = [
    MSG_PROCESSING_STATUS_ID,
    MSG_SENT_ACKNOWLEDGE_STATUS_ID,
    MSG_DELIVERED_STATUS_ID,
    MSG_SEEN_STATUS_ID,
];

window.moment = moment;

// Pinned chats float to the top; the rest keep the newest-first order. Sorting here
// rather than in the query because the server returns every chat and the client
// already decides what to show (archived chats are filtered client-side too).
export const sortBydate = chatMessages => {
    return chatMessages.sort((a, b) => {
        const pinDiff = (b.pinStatus === 1 ? 1 : 0) - (a.pinStatus === 1 ? 1 : 0);
        if (pinDiff !== 0) return pinDiff;
        return b.timestamp > a.timestamp ? 1 : -1;
    });
};

export const sortBydatenewChat = newMessages => {
    return newMessages.sort((a, b) => {
        const { recent: firstchat } = a;
        const { recent: secondchat } = b;
        return new Date(secondchat.time.replace(/ /g, 'T')).getTime() >
            new Date(firstchat.time.replace(/ /g, 'T')).getTime()
            ? 1
            : -1;
    });
};

export const handleSplitUserId = userId => userId?.split('@').shift();

export const isRenderMessageType = messageType => {
    return (
        ['sentMessage', 'carbonSentMessage', 'carbonReceiveMessage', 'receiveMessage', 'carbonSentSeen'].indexOf(
            messageType,
        ) === -1
    );
};

export const changeTimeFormat = time => {
    if (!time) {
        return '';
    } else if (time.toString().length === 16) {
        return moment((time / 1000000) * 1000)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss');
    } else {
        return moment(time).utc().format('YYYY-MM-DD HH:mm:ss');
    }
};

export const timeFormat = time => {
    let offset = moment().utcOffset();
    return moment.utc(time).utcOffset(offset).format('MM/DD/YYYY [at] h:mm:ss A');
};

export const timeFormatForInfo = time => {
    let offset = moment().utcOffset();
    return moment.utc(time).utcOffset(offset).format('YYYY-MM-DD [at] hh:mm A');
};

export const groupstatus = (publisherId, toUserId, status, roster, chatType = CHAT_TYPE_GROUP, msgDisappear) => {
    const publisherName = getContactNameFromRoster(getDataFromRoster(publisherId)) || getFormatPhoneNumber(publisherId);
    const toUserName = getContactNameFromRoster(getDataFromRoster(toUserId)) || getFormatPhoneNumber(toUserId);
    const groupName = isGroupChat(chatType) ? 'the group' : 'broadcast';
    const isPublisherLocalUser = isLocalUser(publisherId);
    const isToUserLocalUser = isLocalUser(toUserId);
    let msgContent;
    if (status === GROUP_CREATED) {
        if (isPublisherLocalUser) {
            msgContent = 'You created ' + groupName;
        } else {
            msgContent = publisherName + ' created ' + groupName;
        }
    } else if (status === GROUP_USER_ADDED) {
        if (isPublisherLocalUser) {
            msgContent = 'You added';
        } else {
            msgContent = publisherName + ' added';
        }
        if (isToUserLocalUser) {
            if (!isPublisherLocalUser) {
                msgContent = msgContent + ' you';
            } else {
                msgContent = '';
            }
        } else {
            msgContent = msgContent + ' ' + toUserName;
        }
    } else if (status === GROUP_USER_REMOVED || status === GROUP_USER_LEFT) {
        if (isPublisherLocalUser) {
            msgContent = 'You ';
        } else {
            msgContent = publisherName;
        }
        if (isToUserLocalUser) {
            if (isPublisherLocalUser) {
                msgContent = msgContent + ' left ';
            } else {
                msgContent = msgContent + ' removed you';
            }
        } else if (isPublisherLocalUser) {
            msgContent = msgContent + ' removed ' + toUserName;
        } else if (toUserId === publisherId) {
            msgContent = msgContent + ' left ';
        } else {
            msgContent = msgContent + ' removed ' + toUserName;
        }
    } else if (status === GROUP_TITLE_UPDATED) {
        if (isPublisherLocalUser) {
            msgContent = 'You changed ' + groupName + ' name';
        } else {
            msgContent = publisherName + ' changed ' + groupName + ' name';
        }
    } else if (status === GROUP_IMAGE_ADDED) {
        if (isPublisherLocalUser) {
            msgContent = 'You added ' + groupName + ' icon';
        } else {
            msgContent = publisherName + ' added ' + groupName + ' icon';
        }
    } else if (status === GROUP_IMAGE_UPDATED) {
        if (isPublisherLocalUser) {
            msgContent = 'You changed ' + groupName + ' icon';
        } else {
            msgContent = publisherName + ' changed ' + groupName + ' icon';
        }
    } else if (status === GROUP_DESCRIPTION_ADDED) {
        if (isPublisherLocalUser) {
            msgContent = 'You added ' + groupName + ' description';
        } else {
            msgContent = publisherName + ' added ' + groupName + ' description';
        }
    } else if (status === GROUP_DESCRIPTION_UPDATED) {
        if (isPublisherLocalUser) {
            msgContent = 'You changed ' + groupName + ' description';
        } else {
            msgContent = publisherName + ' changed ' + groupName + ' description';
        }
    } else if (status === GROUP_PROFILE_INFO_UPDATED) {
        // Server may return the generic 'profileUpdated' status for disappear changes.
        // If msgDisappear is present, treat it as a disappear change for correct banner text.
        if (msgDisappear !== undefined) {
            const label = getDisappearLabel(msgDisappear);
            if (isPublisherLocalUser) {
                msgContent = 'You changed the disappearing message duration to ' + label;
            } else {
                msgContent = publisherName + ' changed the disappearing message duration to ' + label;
            }
        } else if (isPublisherLocalUser) {
            msgContent = 'You changed ' + groupName + ' profile';
        } else {
            msgContent = publisherName + ' changed ' + groupName + ' profile';
        }
    } else if (status === GROUP_USER_MADE_ADMIN) {
        if (isToUserLocalUser) {
            msgContent = 'You are now an admin';
        } else {
            msgContent = toUserName + ' is now an admin';
        }
    } else if (status === GROUP_DISAPPEAR_UPDATED) {
        if (msgDisappear !== undefined && msgDisappear !== null) {
            const label = getDisappearLabel(msgDisappear);
            if (isPublisherLocalUser) {
                msgContent = 'You changed the disappearing message duration to ' + label;
            } else {
                msgContent = publisherName + ' changed the disappearing message duration to ' + label;
            }
        } else {
            if (isPublisherLocalUser) {
                msgContent = 'You changed the disappearing messages';
            } else {
                msgContent = publisherName + ' changed the disappearing messages';
            }
        }
    }
    return msgContent;
};

export const handleMessageParseHtml = (text = '') => {
    if (!text || typeof text !== 'string') return text;

    // Don't escape if the text doesn't contain HTML tags
    // Only escape if there are actual HTML-like structures
    const hasHTMLTags = /<[^>]+>/g.test(text);

    if (!hasHTMLTags) {
        // No HTML tags, return as-is without escaping
        return text;
    }

    // Has HTML tags, escape for safety
    const div = document.createElement('div');
    div.textContent = text; // escape <, >, etc.
    return div.innerHTML; // safe to render
};

// Show large unread in a format like 99+ or 999+ etc.
export const formatUnread = unreadCount => {
    if (isNaN(unreadCount)) {
        return 0;
    }
    return unreadCount > 999 ? '999+' : unreadCount > 99 ? '99+' : unreadCount;
};
